import {makeCresAxiosFoundationAPI} from "../util/AxiosFoundationAPI";

// Taking training, and the record that it happened.
//
// THE ANSWER KEY IS NOT IN THESE TYPES, deliberately: the taking read carries no IsCorrect and no
// Rationale, and the only place either appears is the RESULT of a submitted attempt.

export type TrainingState = "OUTSTANDING" | "OVERDUE" | "COMPLETE";

export type TrainingDue = {
    ObjectUSID: string;
    ObjectRevisionUSID: string;
    ObjectID: string | null;
    Name: string | null;
    ObjectClassification: string | null;
    DisplayRevision: string | null;
    RequiresAcknowledgment: boolean;
    RequiresQuiz: boolean;
    CurriculumName: string | null;
    CurriculumObjectID: string | null;
    State: TrainingState;
    Reason: string;
    CompletedDatetime: string | null;
    Score: number | null;
    RepeatsEveryDays: number | null;
    /** When this falls due. null when the curriculum sets no grace period and nothing repeats. */
    DueDatetime: string | null;
    /** Days past due, computed on the SERVER -- never from the browser's clock. */
    DaysOverdue: number | null;
    GracePeriodDays: number | null;
};

export type QuizToTake = {
    ObjectRevisionUSID: string;
    ObjectID: string | null;
    Name: string | null;
    DisplayRevision: string | null;
    PassingScore: number;
    Questions: {
        USID: string;
        QuestionType: "SINGLE_SELECT" | "MULTI_SELECT" | "TRUE_FALSE";
        Text: string;
        /** How many selections make a complete answer. The taker is told. */
        CorrectSelections: number;
        Choices: {USID: string; Text: string}[];
    }[];
    Attempts: number;
    BestScore: number | null;
    AlreadyPassed: boolean;
};

export type AttemptResult = {
    AttemptUSID: string;
    Score: number;
    Passed: boolean;
    PassingScore: number;
    Questions: {
        QuestionUSID: string;
        Text: string;
        Correct: boolean;
        Credit: number;
        Choices: {USID: string; Text: string; IsCorrect: boolean; Picked: boolean; Rationale: string | null}[];
    }[];
};

export type TrainingRecord = {
    USID: string;
    Who: string | null;
    ObjectID: string | null;
    Name: string | null;
    DisplayRevision: string | null;
    Acknowledged: boolean;
    Score: number | null;
    CompletedDatetime: string | null;
    Signed: boolean;
};

export async function getMyTraining(sessionContext: any): Promise<{Training: TrainingDue[]; Outstanding: number}> {
    const api = makeCresAxiosFoundationAPI(sessionContext);
    return (await api.get("t1/v1/my_training")).data;
}

export async function getQuizToTake(sessionContext: any, objectRevisionUSID: string): Promise<QuizToTake> {
    const api = makeCresAxiosFoundationAPI(sessionContext);
    return (await api.get("t1/v1/quiz_attempts", {params: {ObjectRevisionUSID: objectRevisionUSID}})).data;
}

export async function submitAttempt(sessionContext: any, objectRevisionUSID: string,
                                    answers: {QuestionUSID: string; ChoiceUSID: string}[]): Promise<AttemptResult> {
    const api = makeCresAxiosFoundationAPI(sessionContext);
    return (await api.post("t1/v1/quiz_attempts",
                           {ObjectRevisionUSID: objectRevisionUSID, Answers: answers})).data;
}

/**
 * The signed act. The password goes straight to the server's reauthenticate() and is never stored,
 * echoed or logged -- the same path an approval signature takes.
 */
export async function completeTraining(sessionContext: any, objectRevisionUSID: string,
                                       password: string, acknowledged: boolean): Promise<{Score: number | null}> {
    const api = makeCresAxiosFoundationAPI(sessionContext);
    return (await api.post("t1/v1/training_records",
                           {ObjectRevisionUSID: objectRevisionUSID, Password: password,
                            Acknowledged: acknowledged})).data;
}

export async function getTrainingRecords(sessionContext: any, params: {ObjectUSID?: string; AccountUserUSID?: string}):
        Promise<TrainingRecord[]> {
    const api = makeCresAxiosFoundationAPI(sessionContext);
    return (await api.get("t1/v1/training_records", {params})).data?.Records ?? [];
}

export type TrainingMetrics = {
    /** People in the organization who owe at least one training. */
    People: number;
    Owed: number;
    Complete: number;
    Outstanding: number;
    Overdue: number;
    /** Worst first. The actionable core of the page: a count nobody can act on, a row they can. */
    OverdueRows: {
        Who: string | null;
        AccountUserUSID: string;
        ObjectID: string | null;
        Name: string | null;
        ObjectRevisionUSID: string;
        CurriculumObjectID: string | null;
        CurriculumName: string | null;
        DueDatetime: string | null;
        DaysOverdue: number | null;
    }[];
    /** Which records are blocking the most people — one document nobody has read is a different
     *  problem from scattered stragglers, and the flat list hides the difference. */
    ByRecord: {
        ObjectID: string | null;
        Name: string | null;
        DisplayRevision: string | null;
        OwnerPositionName: string | null;
        Owed: number;
        Complete: number;
        Outstanding: number;
        Overdue: number;
    }[];
    /** How many organization nodes were in scope; null when the persona resolves to none. */
    Scope: number | null;
};

export async function getTrainingMetrics(sessionContext: any): Promise<TrainingMetrics> {
    const api = makeCresAxiosFoundationAPI(sessionContext);
    return (await api.get("t1/v1/training_metrics")).data;
}
